import type { Model } from 'metalworks-domain'

import {
  AttributeSwitchSelector,
  AttributeToggleSelector,
  DistanceDimensionInput,
  MaterialSwatchSelector,
} from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { NonStandardMaterialWarning } from '../NonStandardMaterialWarning'
import { PowderCoatingWarning } from '../PowderCoatingWarning'

interface PlateProps {
  id: Model.Plate['id']
}

const bendSideLengthFixableViolations: string[] = [
  'cornerRadius.genericDimension.tooLarge',
  'bendStartLength.distanceDimension.tooSmall',
  'bendEndLength.distanceDimension.tooSmall',
]

const hasBendFixableViolations: string[] = [
  'bendStartLength.distanceDimension.tooSmall',
  'bendEndLength.distanceDimension.tooSmall',
  'startLengthTooShort',
  'endLengthTooShort',
]

export const cornerRadiusFixableViolations = [
  'bendStartLength.distanceDimension.tooSmall',
  'bendEndLength.distanceDimension.tooSmall',
  'cornerRadius.genericDimension.tooLarge',
  'sideLength.distanceDimension.tooSmall',
]

export const ThicknessProperty = (props: PlateProps) => (
  <AttributeToggleSelector
    entityId={props.id}
    instanceName="thickness"
    fixableViolations={[
      'materialNotAvailable',
      'rawMaterialNotAvailable',
      'finishNotAvailable',
      'countersinkNotAvailable',
      'x.distanceDimension.tooSmall',
      'x.distanceDimension.tooLarge',
      'bendStartLength.distanceDimension.tooSmall',
      'bendEndLength.distanceDimension.tooSmall',
    ]}
  />
)

export const PowderCoatingProperty = (props: PlateProps) => (
  <PropertyContainer.Item>
    <AttributeSwitchSelector
      entityId={props.id}
      instanceName="powderCoating"
      title="Powder Coating"
      description="Add black powder coating finish. This will automatically add a mounting hole if it doesn't exist."
    />
  </PropertyContainer.Item>
)

export const MaterialSwatchProperty = (props: PlateProps) => (
  <MaterialSwatchSelector
    entityId={props.id}
    instanceName="rawMaterial"
    fixableViolations={[
      'rawMaterialNotAvailable',
      'finishNotAvailable',
      'thicknessNotAvailable',
      'countersinkNotAvailable',
    ]}
  />
)

export const FinishProperty = (props: PlateProps) => (
  <PropertyContainer.Item title="Finish">
    <AttributeToggleSelector entityId={props.id} instanceName="finish" />
  </PropertyContainer.Item>
)

export const FinishDirectionProperty = (props: PlateProps) => (
  <PropertyContainer.Item title="Finish Direction">
    <AttributeToggleSelector
      entityId={props.id}
      instanceName="finishDirection"
      fixableViolations={[
        'materialNotAvailable',
        'rawMaterialNotAvailable',
        'thicknessNotAvailable',
        'countersinkNotAvailable',
      ]}
    />
  </PropertyContainer.Item>
)

export const HandingProperty = (props: PlateProps) => (
  <PropertyContainer.Item title="Handing">
    <AttributeToggleSelector entityId={props.id} instanceName="handing" />
  </PropertyContainer.Item>
)

export const NonStandardMaterialWarningWrapper = (props: { standard: boolean }) => (
  <>{!props.standard && <NonStandardMaterialWarning />}</>
)

export const PowderCoatingWarningWrapper = (props: { powderCoating: boolean }) => (
  <>{props.powderCoating && <PowderCoatingWarning />}</>
)

export const HasBendProperty = (props: PlateProps) => (
  <PropertyContainer.Item>
    <AttributeSwitchSelector
      entityId={props.id}
      instanceName="hasBend"
      title="Add bend"
      description="Choose the internal radius closest to your requirement 6.5mm or 11mm."
      fixableViolations={hasBendFixableViolations}
    />
  </PropertyContainer.Item>
)

export const BendProperties = ({ bendId }: { bendId: Model.CustomBend['id'] }) => (
  <>
    <PropertyContainer.Item title="Bend Orientation">
      <AttributeToggleSelector entityId={bendId} instanceName="orientation" />
    </PropertyContainer.Item>
    <PropertyContainer.Item title="Bend Direction">
      <AttributeToggleSelector entityId={bendId} instanceName="direction" />
    </PropertyContainer.Item>
    <PropertyContainer.Item title="Start Length">
      <DistanceDimensionInput
        entityId={bendId}
        instanceName="bendStartLength"
        fixableViolations={bendSideLengthFixableViolations}
      />
    </PropertyContainer.Item>
    <PropertyContainer.Item title="End Length">
      <DistanceDimensionInput
        entityId={bendId}
        instanceName="bendEndLength"
        fixableViolations={bendSideLengthFixableViolations}
      />
    </PropertyContainer.Item>
  </>
)
